/**
 * Utility functions for formatting various data types
 */

/**
 * Format a number as Vietnamese currency (VND)
 * @param amount - The amount to format
 * @returns Formatted currency string
 * @example formatMoney(150000) // "150.000 ₫"
 */
export const formatMoney = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount);
};

/**
 * Format duration string by removing leading zeros
 * @param duration - Duration string in format "HH:MM:SS"
 * @returns Formatted duration string
 * @example formatDuration("00:05:30") // "5:30"
 */
export const formatDuration = (duration: string): string => {
  if (duration === "00:00:00") return "";
  return duration.replace(/^00:/, "");
};

/**
 * Format a date to localized string
 * @param date - Date to format
 * @param locale - Locale to use for formatting (defaults to 'vi-VN')
 * @returns Formatted date string
 * @example formatDate(new Date()) // "01 thg 1, 2024"
 */
export const formatDate = (date: Date, locale = 'vi-VN'): string => {
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a number with thousand separators
 * @param number - Number to format
 * @param locale - Locale to use for formatting (defaults to 'vi-VN')
 * @returns Formatted number string
 * @example formatNumber(1000000) // "1.000.000"
 */
export const formatNumber = (number: number, locale = 'vi-VN'): string => {
  return new Intl.NumberFormat(locale).format(number);
};

/**
 * Format file size in bytes to human readable format
 * @param bytes - Size in bytes
 * @returns Formatted size string
 * @example formatFileSize(1024) // "1 KB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}; 